import { type TipConfiguration, type TipConfigurationScope } from '@/models/account-management/TipConfiguration';
import { HttpError, getJwt } from '../common';

const BASE_URL = import.meta.env.VITE_API_URL;
const PATH = `${BASE_URL}/account-management/tipconfigurations`;

const authHeader = () => ({ 'Authorization': `Bearer ${getJwt()}` });

export const tipConfigurationApi = {
    // All tip configs (brand/group/location scope) defined under a brand.
    list: async (brandId: string): Promise<TipConfiguration[]> => {
        const response = await fetch(`${PATH}?brandId=${brandId}`, { method: 'GET', headers: authHeader() });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new HttpError(response.status, data);
        return data ?? [];
    },
    // The config set at a specific scope target. Returns null when none is set (API sends 200 + null).
    get: async (scopeType: TipConfigurationScope, scopeId: string): Promise<TipConfiguration | null> => {
        const response = await fetch(`${PATH}/${scopeType}/${scopeId}`, { method: 'GET', headers: authHeader() });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new HttpError(response.status, data);
        return data ?? null;
    },
    // The effective (resolved) config for a location: Location > Group (highest priority) > Brand.
    getEffective: async (locationId: string): Promise<TipConfiguration | null> => {
        const response = await fetch(`${PATH}/effective/${locationId}`, { method: 'GET', headers: authHeader() });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new HttpError(response.status, data);
        return data ?? null;
    },
    upsert: async (dto: TipConfiguration): Promise<void> => {
        const response = await fetch(PATH, {
            method: 'PUT',
            body: JSON.stringify(dto),
            headers: { 'Content-Type': 'application/json', ...authHeader() },
        });
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new HttpError(response.status, data);
        }
    },
    remove: async (scopeType: TipConfigurationScope, scopeId: string): Promise<void> => {
        const response = await fetch(`${PATH}/${scopeType}/${scopeId}`, { method: 'DELETE', headers: authHeader() });
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new HttpError(response.status, data);
        }
    },
};
